import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API, OWNER_EMAIL } from "@/lib/api";

const EMPTY = {
  company: "",
  structure_type: "Société",
  siret: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  message: "",
};

const Label = ({ children }) => <>{children}</>;

export const QuoteForm = ({ refsAll = [], brand = "TREMBLAY", selection = [], onRemoveRow, onClearSelection, onSetQty, onSetRowRef, onAddRow }) => {
  const isPanther = brand === "PANTHER";
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const filledRows = selection.filter((s) => s.ref.trim());

  const validate = () => {
    const er = {};
    if (!f.company.trim() || f.company.trim().length < 2) er.company = "Renseignez le nom de votre société, club, association ou établissement scolaire";
    const siretDigits = f.siret.replace(/[\s.]/g, "");
    if (!/^\d{14}$/.test(siretDigits)) er.siret = "Le SIRET doit comporter 14 chiffres";
    if (isPanther) {
      if (!f.reference.trim() || f.reference.trim().length < 2) er.reference = "Indiquez le produit ou la référence PANTHER souhaitée";
    } else if (filledRows.length === 0) {
      er.rows = "Ajoutez au moins une référence : depuis le catalogue ou avec « + Ajouter une ligne »";
    } else {
      const bad = filledRows.filter((s) => {
        const n = parseInt(s.qty, 10);
        return !String(s.qty).trim() || isNaN(n) || n < 1;
      });
      if (bad.length) er.rows = `Quantité obligatoire (minimum 1) pour la référence : ${bad.map((s) => s.ref.trim() || "?").join(", ")}`;
    }
    if (!f.last_name.trim() || f.last_name.trim().length < 2) er.last_name = "Renseignez votre nom";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) er.email = "Adresse e-mail invalide";
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/quote-requests`, {
        ...f,
        brand,
        reference: isPanther ? f.reference : filledRows[0].ref,
        selection: filledRows.map((s) => `${s.ref}${s.name ? ` — ${s.name}` : ""}${s.qty ? ` — Qté : ${s.qty}` : ""}`),
        siret: f.siret.replace(/[\s.]/g, ""),
      });
      toast.success(`Votre demande ${isPanther ? "PANTHER" : ""} a bien été envoyée. Nous revenons vers vous rapidement.`, {
        description: isPanther ? `Référence : ${f.reference}` : `${filledRows.length} référence(s) dans votre demande`,
      });
      setF(EMPTY);
      if (onClearSelection) onClearSelection();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Impossible d'envoyer la demande pour le moment. Réessayez ou écrivez-nous directement.", {
        description: `Contact direct : ${OWNER_EMAIL}`,
      });
    } finally {
      setSending(false);
    }
  };

  const inputCls = "bg-zinc-900 border-zinc-800 text-white placeholder:text-white/25 focus-visible:ring-blaze focus-visible:border-blaze rounded-none h-12";

  return (
    <form onSubmit={submit} noValidate data-testid={`quote-form-${brand.toLowerCase()}`} className="space-y-5">
      {!isPanther && (
        <div data-testid="quote-selection" className="border border-white/10 bg-black/30 p-5">
          <div className="flex items-center justify-between gap-3">
            <p className="font-accent uppercase tracking-[0.22em] text-xs text-white/60">
              Références demandées ({filledRows.length})
            </p>
            <button
              type="button"
              onClick={onAddRow}
              data-testid="quote-add-row"
              className="flex items-center gap-1.5 text-[11px] font-accent uppercase tracking-[0.18em] text-white/70 border border-white/20 px-3 py-1.5 hover:text-blaze hover:border-blaze transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
            </button>
          </div>
          {selection.length === 0 ? (
            <p className="mt-3 text-sm text-white/40">
              Ajoutez autant de références que nécessaire : depuis le catalogue
              (« Ajouter à ma demande ») ou à la main avec « Ajouter une ligne ».
              Indiquez le volume pour chacune.
            </p>
          ) : (
            <div className="mt-4 space-y-2">
              {selection.map((s, i) => (
                <div
                  key={i}
                  data-testid={`quote-selection-row-${i}`}
                  className="flex items-center gap-2 border border-white/10 bg-zinc-900/60 px-3 py-2.5"
                >
                  <div className="flex-1 min-w-0">
                    <input
                      value={s.ref}
                      onChange={(e) => onSetRowRef(i, e.target.value.toUpperCase())}
                      data-testid={`quote-selection-ref-${i}`}
                      placeholder="RÉFÉRENCE (ex : TE1502)"
                      list={refsAll.length ? "catalog-refs" : undefined}
                      className="w-full h-9 bg-black border border-zinc-800 rounded-none px-3 font-mono text-[12px] tracking-wider text-blaze placeholder:text-white/25 focus:outline-none focus:border-blaze"
                    />
                    {s.name && <p className="text-[11px] text-white/50 mt-1 truncate">{s.name}</p>}
                  </div>
                  <input
                    value={s.qty || ""}
                    onChange={(e) => onSetQty(i, e.target.value)}
                    data-testid={`quote-selection-qty-${i}`}
                    placeholder="Qté"
                    inputMode="numeric"
                    aria-label={`Quantité ligne ${i + 1}`}
                    className={`w-16 h-9 shrink-0 bg-black rounded-none text-center text-sm text-white placeholder:text-white/25 focus:outline-none ${errors.rows && !String(s.qty).trim() ? "border border-blaze" : parseInt(s.qty, 10) >= 1 ? "border border-zinc-800" : errors.rows ? "border border-blaze" : "border border-zinc-800"}`}
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveRow(i)}
                    data-testid={`quote-selection-remove-${i}`}
                    className="text-white/40 hover:text-blaze transition-colors shrink-0"
                    aria-label={`Retirer la ligne ${i + 1}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {errors.rows && <p data-testid="quote-error-rows" className="mt-3 text-blaze text-xs">{errors.rows}</p>}
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <label className="flex flex-col gap-2">
          <Label required>Société / Club / Association / Établissement scolaire</Label>
          <select
            data-testid="quote-input-structure-type"
            className={inputCls}
            value={f.structure_type}
            onChange={set("structure_type")}
          >
            <option value="Société">Société</option>
            <option value="Club">Club</option>
            <option value="Association">Association</option>
            <option value="Établissement scolaire">Établissement scolaire</option>
          </select>
          <Input data-testid="quote-input-company" aria-label="Nom de la structure" className={inputCls} value={f.company} onChange={set("company")} placeholder="Nom exact de votre structure" />
          {errors.company && <span data-testid="quote-error-company" className="text-blaze text-xs">{errors.company}</span>}
        </label>
        <label className="flex flex-col gap-2">
          <Label required>SIRET (14 chiffres)</Label>
          <Input
            data-testid="quote-input-siret"
            className={inputCls}
            value={f.siret}
            onChange={set("siret")}
            placeholder="123 456 789 00012"
            inputMode="numeric"
          />
          {errors.siret && <span data-testid="quote-error-siret" className="text-blaze text-xs">{errors.siret}</span>}
        </label>
      </div>

      {isPanther && (
        <label className="flex flex-col gap-2">
          <Label required>Produit / référence PANTHER</Label>
          <Input
            data-testid="quote-input-reference"
            className={`${inputCls} font-mono tracking-widest uppercase`}
            value={f.reference}
            onChange={set("reference")}
            placeholder="Référence ou désignation du produit PANTHER"
          />
          {errors.reference && <span data-testid="quote-error-reference" className="text-blaze text-xs">{errors.reference}</span>}
        </label>
      )}
      {refsAll.length > 0 && (
        <datalist id="catalog-refs">
          {refsAll.slice(0, 900).map((r) => (
            <option key={r} value={r} />
          ))}
        </datalist>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <label className="flex flex-col gap-2">
          <Label>Prénom</Label>
          <Input data-testid="quote-input-firstname" className={inputCls} value={f.first_name} onChange={set("first_name")} placeholder="Prénom" />
        </label>
        <label className="flex flex-col gap-2">
          <Label required>Nom</Label>
          <Input data-testid="quote-input-lastname" className={inputCls} value={f.last_name} onChange={set("last_name")} placeholder="Nom" />
          {errors.last_name && <span data-testid="quote-error-lastname" className="text-blaze text-xs">{errors.last_name}</span>}
        </label>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <label className="flex flex-col gap-2">
          <Label required>E-mail</Label>
          <Input data-testid="quote-input-email" type="email" className={inputCls} value={f.email} onChange={set("email")} placeholder="vous@societe.fr" />
          {errors.email && <span data-testid="quote-error-email" className="text-blaze text-xs">{errors.email}</span>}
        </label>
        <label className="flex flex-col gap-2">
          <Label>Téléphone</Label>
          <Input data-testid="quote-input-phone" className={inputCls} value={f.phone} onChange={set("phone")} placeholder="06 12 34 56 78" />
        </label>
      </div>

      <label className="flex flex-col gap-2">
        <Label>Message</Label>
        <Textarea data-testid="quote-input-message" className={`${inputCls} min-h-28`} value={f.message} onChange={set("message")} placeholder="Précisez votre besoin (coloris, tailles, délais...)" />
      </label>

      <button
        type="submit"
        disabled={sending}
        data-testid="quote-form-submit"
        className="w-full sm:w-fit px-10 py-4 bg-blaze text-white font-accent uppercase tracking-[0.25em] text-sm hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(200,16,46,0.35)] transition-all duration-200 disabled:opacity-60 disabled:translate-y-0"
      >
        {sending ? "Envoi en cours..." : `Envoyer ma demande ${isPanther ? "PANTHER" : ""}`}
      </button>
      <p className="text-xs text-white/40">
        Aucun paiement en ligne. Le SIRET et au moins une référence sont obligatoires
        {!isPanther && filledRows.length > 0 ? ` — votre demande contient ${filledRows.length} référence(s)` : ""}.
      </p>
    </form>
  );
};
